import { useMemo, useState } from "react";
import { CheckCircle2, Loader2, PlugZap, Unplug } from "lucide-react";
import toast from "react-hot-toast";

import {
  chatbotV2Service,
  type ChannelAccountStatus,
  type ChannelOAuthOption,
  type ChatChannel,
} from "../../services/chatbotV2/chatbotV2Service";
import { isChannelConnected } from "../../utils/normalizeChannelsOverview";
import ModalInstagramIntegration from "./Modals/ModalInstagramIntegration";
import ModalMessengerIntegration from "./Modals/ModalMessengerIntegration";
import { completeChannelOAuthWithSelection } from "./hooks/channelOAuthFlow";
import { invalidateChannelCaches } from "./hooks/useChatChannelConnections";
import { Language } from "../../utils/dictionary";
import { INTEGRATIONS_UI_CONFIG } from "./config/integrations.config";

type Surface = "chatbot" | "global";

type ChannelConnectionDetailProps = {
  channel: ChatChannel;
  account: ChannelAccountStatus | null;
  surface?: Surface;
  language?: Language;
  actionLoading?: string | null;
  onRefresh?: () => void | Promise<void>;
  onDisconnect?: () => void | Promise<void>;
  onDiagnose?: () => Promise<Record<string, unknown> | null>;
  onTestSend?: (recipient: string, text: string) => Promise<Record<string, unknown> | null>;
};

const channelTitle = (channel: ChatChannel) => INTEGRATIONS_UI_CONFIG[channel].name;

export const ChannelConnectionDetail = ({
  channel,
  account,
  surface = "chatbot",
  language = Language.SPANISH,
  actionLoading,
  onRefresh,
  onDisconnect,
  onDiagnose,
  onTestSend,
}: ChannelConnectionDetailProps) => {
  const [connectOpen, setConnectOpen] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);
  const [oauthOptions, setOauthOptions] = useState<ChannelOAuthOption[]>([]);
  const [oauthSessionId, setOauthSessionId] = useState<string | null>(null);
  const [diagnoseResult, setDiagnoseResult] = useState<Record<string, unknown> | null>(null);
  const [testRecipient, setTestRecipient] = useState("");
  const [testText, setTestText] = useState("Mensaje de prueba de Fluxi.");
  const connected = isChannelConnected(account);
  const busy = Boolean(actionLoading?.startsWith(`${channel}:`) || connectLoading);

  const subtitle = useMemo(() => {
    if (surface === "chatbot") return "Se sincroniza con Integraciones";
    return "También disponible en Chatbot";
  }, [surface]);

  const handleOAuthCode = async (data: { code: string }) => {
    setConnectLoading(true);
    try {
      const preview = await chatbotV2Service.channelOAuthPreview(channel, { code: data.code });
      if (!preview.selection_required) {
        setConnectOpen(false);
        setOauthOptions([]);
        setOauthSessionId(null);
        invalidateChannelCaches();
        await onRefresh?.();
        toast.success(`${channelTitle(channel)} conectado`);
        return;
      }
      setOauthSessionId(preview.oauth_session_id || null);
      setOauthOptions(preview.options || []);
      if ((preview.options || []).length === 1 && preview.oauth_session_id) {
        await completeChannelOAuthWithSelection(channel, preview.oauth_session_id, preview.options![0]);
        setConnectOpen(false);
        setOauthOptions([]);
        setOauthSessionId(null);
        invalidateChannelCaches();
        await onRefresh?.();
        toast.success(`${channelTitle(channel)} conectado`);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "No se pudo conectar el canal";
      if (message !== "selection_required") {
        toast.error(message);
      }
    } finally {
      setConnectLoading(false);
    }
  };

  const handleSelectOption = async (option: ChannelOAuthOption) => {
    if (!oauthSessionId) return;
    setConnectLoading(true);
    try {
      await completeChannelOAuthWithSelection(channel, oauthSessionId, option);
      setConnectOpen(false);
      setOauthOptions([]);
      setOauthSessionId(null);
      invalidateChannelCaches();
      await onRefresh?.();
      toast.success(`${channelTitle(channel)} conectado`);
    } catch {
      toast.error("No se pudo completar la conexión");
    } finally {
      setConnectLoading(false);
    }
  };

  const ConnectModal =
    channel === "messenger" ? ModalMessengerIntegration : ModalInstagramIntegration;

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-gray-400">{subtitle}</p>
        <h2 className="mt-1 text-xl font-semibold text-gray-950">{channelTitle(channel)}</h2>
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
            {connected ? (
              <CheckCircle2 className="size-4 text-emerald-600" />
            ) : (
              <PlugZap className="size-4 text-gray-400" />
            )}
            {connected ? "Conectado" : "Sin conectar"}
          </div>
          {connected ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => void onDisconnect?.()}
              className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Unplug className="size-3.5" />}
              Desconectar
            </button>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={() => setConnectOpen(true)}
              className="rounded-full bg-gray-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-gray-800 disabled:opacity-60"
            >
              Conectar
            </button>
          )}
        </div>

        {account ? (
          <dl className="mt-4 grid gap-3 text-sm text-gray-600 sm:grid-cols-2">
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">Estado</dt>
              <dd>{account.status || (connected ? "connected" : "not_connected")}</dd>
            </div>
            {account.display_name ? (
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">Cuenta</dt>
                <dd>{account.display_name}</dd>
              </div>
            ) : null}
            {account.page_id ? (
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">Page ID</dt>
                <dd className="break-all font-mono text-xs">{account.page_id}</dd>
              </div>
            ) : null}
            {account.ig_user_id || (account as { instagram_business_account_id?: string }).instagram_business_account_id ? (
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">IG Business ID</dt>
                <dd className="break-all font-mono text-xs">
                  {account.ig_user_id ||
                    (account as { instagram_business_account_id?: string }).instagram_business_account_id}
                </dd>
              </div>
            ) : null}
            {account.webhook_url ? (
              <div className="sm:col-span-2">
                <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">Webhook</dt>
                <dd className="break-all font-mono text-xs">{account.webhook_url}</dd>
              </div>
            ) : null}
          </dl>
        ) : null}
      </div>

      {connected ? (
        <div className="space-y-4 rounded-2xl border border-gray-100 bg-white p-4">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={async () => setDiagnoseResult((await onDiagnose?.()) || null)}
              className="rounded-full border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              Diagnosticar
            </button>
          </div>
          <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
            <input
              value={testRecipient}
              onChange={(event) => setTestRecipient(event.target.value)}
              placeholder={channel === "messenger" ? "PSID o ID destinatario" : "IG scoped ID destinatario"}
              className="h-10 rounded-lg border border-gray-200 px-3 text-sm outline-none focus:border-gray-400"
            />
            <button
              type="button"
              disabled={busy || !testRecipient.trim()}
              onClick={() => void onTestSend?.(testRecipient.trim(), testText)}
              className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-60"
            >
              Enviar prueba
            </button>
          </div>
          {diagnoseResult ? (
            <pre className="max-h-48 overflow-auto rounded-lg bg-gray-50 p-3 text-xs text-gray-700">
              {JSON.stringify(diagnoseResult, null, 2)}
            </pre>
          ) : null}
        </div>
      ) : null}

      {oauthOptions.length > 1 && oauthSessionId ? (
        <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-amber-900">Selecciona la cuenta a conectar</p>
          <div className="mt-3 space-y-2">
            {oauthOptions.map((option) => (
              <button
                key={`${option.page_id}-${option.instagram_business_account_id || ""}`}
                type="button"
                disabled={busy}
                onClick={() => void handleSelectOption(option)}
                className="flex w-full items-center justify-between rounded-xl border border-amber-100 bg-white px-3 py-2 text-left text-sm hover:bg-amber-50/60 disabled:opacity-60"
              >
                <span>
                  {option.page_name || option.username || option.page_id}
                  {option.username ? ` (@${option.username})` : ""}
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {connectOpen ? (
        <ConnectModal
          language={language}
          loading={connectLoading}
          setModalOpen={setConnectOpen}
          handleOAuthComplete={handleOAuthCode}
        />
      ) : null}
    </div>
  );
};
